import React from 'react'
import PropTypes from 'prop-types'
import { observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { isEmpty } from 'lodash'
import { focus, isAndroidDevice } from 'core/utils'
import { Icon, FreeChatWindowOfferStrip } from 'components/misc'
import { EmojiPicker, StickerPicker } from './Pickers'
import { FileUploadButton } from '../misc/FileUploadButton'
import { SexIcons } from '../misc/SexIcons'
import Gift from '../misc/Gift'
import { MESSAGE_MAX_CHARS, MODES } from 'core/constants'
import request from 'core/request'
import * as endpoints from '../../../core/endpoints'
import GiftSuggestion from './GiftsSuggestion';
import AudioMessage from '../misc/AudioMessage';
import CONFIG from 'core/globals'
import { freeChatWindowEnds, latestFreeChatWindowEndsAt } from 'core/freeChatWindow'
import { openUpgrade } from 'core/openUpgrade'

@inject('store', 'actions')
@observer
class ChatInput extends React.Component {
  state = {
    sendIndicator: true,
    isGalleryShown: false,
    settingsFS: null,
    giftPrices: null,
  }
  @observable message = ''
  @observable loading = false
  @observable wordCounter = 0

  async componentDidMount() {
    if (!isAndroidDevice) {
      focus(this.props.inputRef)
    }
      try {
        const featureSwitches = await request.get(endpoints.CCP_GET_CONFIG);
        const prices = await request.get(endpoints.GIFT_PRICES)
        // AVN-1396: the server decides when the free chat window and side C's discount end, we only mirror them.
        const ends = freeChatWindowEnds({
          secondsLeft: featureSwitches.freeChatWindowSecondsLeft,
          endsAt: featureSwitches.freeChatWindowEndsAt,
          discountSecondsLeft: featureSwitches.freeChatWindowDiscountSecondsLeft,
        }, Math.floor(Date.now() / 1000));
        this.props.store.freeChatWindowEndsAt = latestFreeChatWindowEndsAt(this.props.store.freeChatWindowEndsAt, ends.endsAt);
        this.props.store.freeChatWindowDiscountEndsAt = latestFreeChatWindowEndsAt(this.props.store.freeChatWindowDiscountEndsAt, ends.discountEndsAt);
        this.props.store.freeChatWindowTestSide = featureSwitches.freeChatWindowTestSide || null;
        this.setState({ settingsFS: featureSwitches, giftPrices: JSON.parse(prices.nameToPrice) });
      } catch (error) {
        console.error('Failed to fetch:', error);
      }
  }

  send = async message => {
    const { onMessageSend } = this.props.options.hooks
    const conv = this.props.store.conversation

    conv.readConversation()

    if (isEmpty(message.trim())) {
      return
    }

    // AVN-1396 side C: while the post-window discount runs, a message goes straight to the upgrade page instead of
    // into the usual out-of-credits handling, which stays as it is for everyone else.
    if (this.props.store.freeChatWindowDiscountActive) {
      openUpgrade(CONFIG.urls.upgrade(conv.otherUserProfile), CONFIG)
      return
    }

    this.loading = true
    this.stopTypingIndicator()

    await conv.send(message)
    onMessageSend({ message, from: conv.meUid, to: conv.otherUserUid })

    this.loading = false
    const { conversation } = this.props.store
    if (conversation?.featuredApUnlock.purchaseSuccess && conversation?.featuredApUnlock.isChatLocked) {
      conversation.featuredApUnlock.isChatLocked = false;
    }
  }

  onSubmit = async e => {
    e.preventDefault()
    await this.send(this.message)

    this.message = ''
    this.wordCounter = 0
    focus(this.props.inputRef)
  }

  onEmojiSelect = emoji => {
    this.message += (this.message ? ' ' : '') + emoji
  }
  onClickEmoji = () => {
    this.setState(prevState => ({
      isGalleryShown: !prevState.isGalleryShown,
    }))
  }
  onClickSexIcons = () => {
    this.setState({isGalleryShown: false})
  }
  onStickerSelect = async stickerId => {
    await this.send(`:sticker-${stickerId}:`)
  }

  onChange = async e => {
    const { conversation } = this.props.store
    const message = e.target.value
    this.message = message
    this.wordCounter = message.length
    if (conversation.lastMessageSentAt > 0 && conversation.meMessageCount > 1) {
      if (message.length === 0 || message.length > MESSAGE_MAX_CHARS) {
        this.stopTypingIndicator()
      } else if (this.state.sendIndicator) {
        conversation.sendTypingIndicator(message.length)
        this.setState({ sendIndicator: false })
        setTimeout(() => this.setState({ sendIndicator: true }), 1500)
      }
    }
  }

  stopTypingIndicator = async() => {
    const { conversation } = this.props.store
    if (conversation.lastMessageSentAt > 0 && conversation.meMessageCount > 1) {
      await conversation.sendTypingIndicator(0)
      this.setState({ sendIndicator: true })
    }
  }

  readConversation = () => this.props.store.conversation.readConversation()

  render() {
    const { stickers, translations: { basic }, features, mode } = this.props.options
    const { store: { conversation, user } } = this.props
    const hasStickers = !!stickers?.length
    const placeholderText = this.props.creditPlaceholderText
    const sexiconsEnabledFS = features.sexicons
    const giftsEnabledFS = features.gifts
    const audioMessageFS = features.audioMessageFS
    const hasFreeCredits = features.hasFreeCredits
    const isExternalUpgradeSite = features.isExternalUpgradeSite
    const shouldShowGiftsSuggestion = (conversation.messages.length < 5) && giftsEnabledFS && user.premium && this.state.giftPrices
    return (
      <form className="chat-compose" onSubmit={this.onSubmit}>
        <FreeChatWindowOfferStrip/>
        {hasStickers && (
          <div className="chat-items-picker-container">
            <EmojiPicker onSelect={this.onEmojiSelect} label="smileys"/>
            <span className="chat-item-picker-divider"/>
            <StickerPicker stickers={stickers} onSelect={this.onStickerSelect} label="stickers"/>
            { features.canUploadImages && <span className="chat-item-picker-divider"/> }
            <FileUploadButton conversation={conversation} user={user} label="photo"/>
          </div>
        )}
        <div className="chat-footer-flex-wrapper"> { /*needed for IE11*/ }
          {shouldShowGiftsSuggestion && <GiftSuggestion conversation={conversation} prices={this.state.giftPrices}/>}
          {!hasStickers && (
          <div className="chat-options">  { /*needed for IE11*/ }
            <div className="chat-items-picker-container chat-items-picker-container--inline">
              <div onClick={this.onClickEmoji}><EmojiPicker onSelect={this.onEmojiSelect}/></div>
              {sexiconsEnabledFS && (
                <div onClick={this.onClickSexIcons}>
                  <SexIcons conversation={conversation} areEmojisShown={this.state.isGalleryShown} user={this.props.store.user}/>
                </div>
              )}
             {giftsEnabledFS && (user.premium || hasFreeCredits) && this.state.giftPrices && <Gift conversation={conversation} giftPrices={this.state.giftPrices} mode={mode}/>}
              <FileUploadButton conversation={conversation} user={user}/>
            </div>
            <div id="chat-word-counter">{this.wordCounter}/{MESSAGE_MAX_CHARS}</div>
            {/* Due to some bundling without explicit null renders 0 in the DOM */}
            {(audioMessageFS && isExternalUpgradeSite) ? <AudioMessage/> : null}
          </div>
        )}
          <div className="chat-input-container">
            <div className="chat-input-wrapper">
              <input
                name="message"
                autoComplete="off"
                tabIndex={isAndroidDevice && -1}
                placeholder={placeholderText == null ? basic.typeAMessage : placeholderText}
                className={`${placeholderText != null ? 'credit-input' : ''} chat-input`}
                maxLength={MESSAGE_MAX_CHARS}
                disabled={this.loading}
                onChange={this.onChange}
                onFocus={() => {
                  this.readConversation();
                  if (window.innerWidth <= 550 && mode === MODES.CHAT_LIST) {
                    CONFIG.isNuxt ? document.querySelector('body')?.classList.add('no-scroll') : document.querySelector('.body-container')?.classList.add('no-scroll')
                    this.props.setVhVariable()
                  }
                }}
                value={this.message}
                ref={this.props.inputRef}
                autoFocus={!isAndroidDevice}
                onBlur={() => {
                  if (window.innerWidth <= 550 && mode === MODES.CHAT_LIST) {
                    CONFIG.isNuxt ? document.querySelector('body')?.classList.remove('no-scroll') : document.querySelector('.body-container')?.classList.remove('no-scroll')
                    this.props.setVhVariable()
                  }
                }}
              />
              <button type="submit" disabled={this.loading} className="chat-input-button">
                {mode !== MODES.CHAT_LIST && (basic.send || <Icon iconName="send" className="stroke"/>)}
                {mode === MODES.CHAT_LIST && <Icon iconName="telegram" className="telegram-icon"/>}
              </button>
            </div>
          </div>
        </div>
      </form>
    )
  }
}

ChatInput.propTypes = {
  conversation: PropTypes.object,
}

export default ChatInput
